import React, { useEffect, useState, useContext } from 'react'
import SimpleBar from 'simplebar-react'
import 'simplebar/dist/simplebar.min.css'
import {
  CButton,
  CCard,
  CCol,
  CForm,
  CFormInput,
  CFormLabel,
  CFormText,
  CModal,
  CModalBody,
  CRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilPencil } from '@coreui/icons'
import { GlobalContext } from '../../context/GlobalState'
import { AppContext } from '../../context/app'
import { PERMISSIONS } from '../../utils/permissions'
import { checkPermissions, raiseToast } from '../../utils/helper'
import { isEmpty } from 'lodash'
import useAxios from 'axios-hooks'
import { CUSTOMER_ORGS_API, MASTER_ORGS_API } from '../../utils/urls'

const PaymentGateway = () => {
  const { setLoader } = useContext(AppContext)
  const [paymentOrgs, setPaymentOrgs] = useState([])
  const [customerPaymentOrgs, setCustomerPaymentOrgs] = useState([])

  const [visible, setVisible] = useState(false)

  const [selectedGateway, setSelectedGateway] = useState({})
  const [gatewayInputs, setGatewayInputs] = useState([])

  const [editGatewayId, setEditGatewayId] = useState('')
  const { userSessionObj } = useContext(GlobalContext)

  const createAllowed = checkPermissions(
    userSessionObj.session_data.permissions,
    PERMISSIONS.PAYMENT_GATEWAY.CREATE,
  )
  const updateAllowed = checkPermissions(
    userSessionObj.session_data.permissions,
    PERMISSIONS.PAYMENT_GATEWAY.UPDATE,
  )

  const [{ data: masterOrgsList, loading: masterOrgsLoading }] = useAxios({
    url: MASTER_ORGS_API,
    method: 'get',
  })

  const [{ data: customerOrgsList, loading: customerOrgsLoading }, customerOrgsListCall] = useAxios(
    {
      url: CUSTOMER_ORGS_API,
      method: 'get',
    },
    { manual: true },
  )
  const [
    { data: createPaymentDetail, loading: createPaymentDetailLoading },
    createPaymentDetailCall,
  ] = useAxios(
    {
      url: CUSTOMER_ORGS_API,
      method: 'post',
    },
    { manual: true },
  )
  const [
    { data: verifyPaymentDetail, loading: verifyPaymentDetailLoading },
    verifyPaymentDetailCall,
  ] = useAxios(
    {
      url: `${CUSTOMER_ORGS_API}/verify`,
      method: 'put',
    },
    { manual: true },
  )
  const [{ data: updateCustomerOrg, loading: updateCustomerOrgLoading }, updateCustomerOrgCall] =
    useAxios(
      {
        url: CUSTOMER_ORGS_API,
        method: 'put',
      },
      { manual: true },
    )

  useEffect(() => {
    customerOrgsListCall()
  }, [])

  useEffect(() => {
    setLoader(
      masterOrgsLoading ||
        customerOrgsLoading ||
        createPaymentDetailLoading ||
        verifyPaymentDetailLoading ||
        updateCustomerOrgLoading,
    )
  }, [
    masterOrgsLoading,
    customerOrgsLoading,
    createPaymentDetailLoading,
    verifyPaymentDetailLoading,
    updateCustomerOrgLoading,
  ])

  useEffect(() => {
    if (masterOrgsList && masterOrgsList.success) {
      setPaymentOrgs(masterOrgsList.data)
    }
  }, [masterOrgsList])

  useEffect(() => {
    if (customerOrgsList && customerOrgsList.success) {
      setCustomerPaymentOrgs(customerOrgsList.data)
    }
  }, [customerOrgsList])

  useEffect(() => {
    if (createPaymentDetail && createPaymentDetail.success) {
      customerOrgsListCall()
      handleCancel()
      raiseToast('success', 'Created successfully')
    }
  }, [createPaymentDetail])

  useEffect(() => {
    if (verifyPaymentDetail && verifyPaymentDetail.success) {
      customerOrgsListCall()
      raiseToast('success', 'Verified successfully')
    }
  }, [verifyPaymentDetail])

  useEffect(() => {
    if (updateCustomerOrg) {
      if (updateCustomerOrg.success) {
        customerOrgsListCall()
        handleCancel()
        raiseToast('success', updateCustomerOrg?.message)
      } else {
        raiseToast('error', updateCustomerOrg?.message)
      }
    }
  }, [updateCustomerOrg])

  const onValueChange = (event, keyname, index) => {
    gatewayInputs[index][keyname] = event.target.value
    setGatewayInputs([...gatewayInputs])
  }

  const validateInfo = () => {
    let isFormValid = true
    for (let i = 0; i < gatewayInputs.length; i++) {
      const element = gatewayInputs[i]
      if (element[selectedGateway.required_keys[i]].length === 0) {
        isFormValid = false
        break
      }
    }
    return isFormValid
  }

  const handleCreate = async () => {
    if (!createAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }

    if (validateInfo()) {
      let details = Object.assign({}, ...gatewayInputs)

      let data = { gateway_org_id: selectedGateway._id, details: details }
      createPaymentDetailCall({ data })
    } else {
      raiseToast('error', 'No value change / Empty values')
    }
  }

  const handleVerify = async (ele) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    if (ele && !isEmpty(ele)) {
      verifyPaymentDetailCall({
        params: {
          customer_gateway_id: ele._id,
        },
      })
    }
  }

  const handleUpdate = async () => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    if (validateInfo()) {
      let details = Object.assign({}, ...gatewayInputs)

      updateCustomerOrgCall({
        params: {
          customer_gateway_id: editGatewayId,
        },
        data: { details },
      })
    } else {
      raiseToast('error', 'No value change / Empty values')
    }
  }

  const handleSelectGateway = (ele) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    let tempArr = gatewayInputs
    ele.required_keys.forEach((element) => {
      let obj = {}
      obj[`${element}`] = ''
      tempArr.push(obj)
    })
    setGatewayInputs(tempArr)
    setSelectedGateway(ele)
    setVisible(true)
  }

  const handleCancel = () => {
    setVisible(false)
    setSelectedGateway({})
    setGatewayInputs([])
    setEditGatewayId('')
  }

  const handleEdit = (obj, editobj) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    handleSelectGateway(obj)
    setEditGatewayId(editobj._id)
    // let input = Object.entries(editobj.details).map((e) => ({ [e[0]]: e[1] }))
    // setGatewayInputs(input)
    // setSelectedGateway(obj)
    // setVisible(true)
  }
  return (
    <>
      <h4>Payment Gateway</h4>
      <CFormText style={{ color: '#707070' }}>
        Configure the payment gateway from the list below
      </CFormText>
      <CCard className={'mt-2'}>
        <SimpleBar>
          <div className="mt-5 mb-3" style={{ marginLeft: '2rem', marginRight: '2rem' }}>
            {/* <CContainer style={{ marginLeft: '2rem', border: '2px solid red' }}> */}
            <CRow xs={{ cols: 2 }} className="mt-3">
              {!isEmpty(paymentOrgs)
                ? paymentOrgs.map((obj) => {
                    let verifyObj = customerPaymentOrgs.find((ele) => ele.gateway_org === obj._id)
                    console.log(verifyObj ? 'true' : 'false')
                    return (
                      <CCol
                        className="mb-5"
                        style={{
                          marginLeft: '4.3rem',
                          // border: '1px solid blue',
                          boxShadow:
                            '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 10px 0 rgba(0, 0, 0, 0.19)',
                          backgroundColor: '#fafafa',
                          height: '13rem',
                        }}
                        xs="5"
                        key={obj.org}
                      >
                        <CRow>
                          <CCol className="mt-3" style={{ marginLeft: '1.5rem' }}>
                            <div className="imgage-div-payment mb-2">
                              <img className="imgage-div-payment" src={obj.image_url} alt={'img'} />
                            </div>
                            {/* <p>{obj.org}</p> */}
                            <p style={{ fontSize: '0.9rem' }}>{obj.description}</p>
                          </CCol>
                          <CCol xs="3" style={{ marginRight: '1.2rem' }}>
                            <div style={{ marginTop: '6rem' }} className="d-flex align-items-end">
                              <CButton
                                className="text-white mt-5"
                                color={
                                  verifyObj
                                    ? verifyObj?.is_account_verified
                                      ? 'success'
                                      : 'warning'
                                    : 'info'
                                }
                                onClick={
                                  verifyObj
                                    ? verifyObj?.is_account_verified
                                      ? null
                                      : () => handleVerify(verifyObj)
                                    : () => handleSelectGateway(obj)
                                }
                                disabled={!createAllowed || !updateAllowed}
                              >
                                {verifyObj
                                  ? verifyObj?.is_account_verified
                                    ? 'Verfied'
                                    : 'verify'
                                  : 'Connect'}
                              </CButton>
                              <div className="d-flex align-items-end">
                                {verifyObj && (
                                  <CIcon
                                    className="m-2"
                                    icon={cilPencil}
                                    size="xl"
                                    title="Edit Plan"
                                    onClick={() => handleEdit(obj, verifyObj)}
                                  />
                                )}
                              </div>
                            </div>
                          </CCol>
                        </CRow>
                      </CCol>
                    )
                  })
                : null}
            </CRow>
            {/* </CContainer> */}
          </div>
        </SimpleBar>
      </CCard>
      <CModal alignment="center" visible={visible}>
        <CModalBody>
          <CForm>
            <CFormLabel>Connect {selectedGateway.org}</CFormLabel>
            <CRow>
              {!isEmpty(selectedGateway)
                ? selectedGateway.required_keys.map((info, index) => {
                    return (
                      <CRow className="mb-3 mt-3" key={index}>
                        <CCol>
                          <h6>Your {info}</h6>
                          <CFormInput
                            value={gatewayInputs[info]}
                            onChange={(e) => onValueChange(e, info, index)}
                          />
                        </CCol>
                      </CRow>
                    )
                  })
                : null}
            </CRow>
            <CRow className="mt-2">
              <CCol>
                <div className="pr-5 d-md-flex justify-content-md-end">
                  <CButton
                    className="text-white"
                    color="info"
                    style={{ marginRight: '2rem' }}
                    onClick={handleCancel}
                  >
                    cancel {/* {confirmationVisible ? `Update` : `Cancel`} */}
                  </CButton>
                  <CButton
                    style={{ marginRight: '1.6rem' }}
                    className="text-white px-4"
                    color="info"
                    onClick={editGatewayId ? handleUpdate : handleCreate}
                  >
                    {editGatewayId ? `Update` : `Connect`}
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </CForm>
        </CModalBody>
      </CModal>
    </>
  )
}

export default PaymentGateway
